import * as microsoftTeams from "@microsoft/teams-js";
import { FrameContexts } from "@microsoft/teams-js";
import type { AjaxError } from "rxjs/ajax";
import { isActionOf, type RootAction } from "typesafe-actions";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { sendMessageToWorkspace } from "../../post-message-dispatch";
import { updateDocumentTitle } from "../../services/dependencies/workspaceDependencies";
import { appendTeamsMode } from "../../utils/url.helpers";
import { updateFlags } from "../config/config.actions";
import type { AppContext, AppState } from "../root.reducer";
import * as userActions from "../user/user.actions";
import { retryableFailures } from "../workspaces/retryableFailures";
import * as workspacesActions from "../workspaces/workspaces.actions";
import {
  selectDashboardWorkspaceDetailsById,
  selectWorkspaceById,
} from "../workspaces/workspaces.selector";
import * as modeActions from "./mode.actions";

export enum AppMode {
  LOADING_DASHBOARD = "LOADING_DASHBOARD",
  NOT_LOGGED_IN = "NOT_LOGGED_IN",
  BETA_NOT_ALLOWED = "BETA_NOT_ALLOWED",
  SKIPPABLE_VERIFIED_POLICY_RESTRICTED = "SKIPPABLE_VERIFIED_POLICY_RESTRICTED",
  NOT_VERIFIED_POLICY_RESTRICTED = "NOT_VERIFIED_POLICY_RESTRICTED",
  DASHBOARD = "DASHBOARD",
  LOADING_WORKSPACE = "LOADING_WORKSPACE",
  EDITING = "EDITING",
  ERROR = "ERROR",
}

// State diagram: https://app.hoylu.com/181819150

export default (state: AppState | undefined, action: RootAction): AppMode => {
  if (state?.mode === undefined) return AppMode.LOADING_DASHBOARD;

  const fromAnywhereMode = fromAnywhere(state, action);
  if (fromAnywhereMode !== state.mode) return fromAnywhereMode;
  switch (state.mode) {
    case AppMode.LOADING_DASHBOARD:
      return fromLoadingDashboard(state.context, action);
    case AppMode.NOT_LOGGED_IN:
      return fromNotLoggedIn(state.context, action);
    case AppMode.DASHBOARD:
      return fromDashboard(state.context, action);
    case AppMode.LOADING_WORKSPACE:
      return fromLoadingWorkspace(state.context, action);
    case AppMode.EDITING:
      return fromEditing(state.context, action);
    case AppMode.NOT_VERIFIED_POLICY_RESTRICTED:
    case AppMode.SKIPPABLE_VERIFIED_POLICY_RESTRICTED:
      return fromNotVerified(state, action);
    case AppMode.ERROR:
      return fromError(state.context, action);
    default:
      return state.mode;
  }
};

function fromAnywhere(state: AppState, action: RootAction) {
  if (
    isActionOf([userActions.logout.success, userActions.logout.failure], action)
  )
    return AppMode.NOT_LOGGED_IN;
  if (
    isActionOf(updateFlags, action) &&
    state.context.config.isBeta &&
    action.payload.isAnonymous === false &&
    action.payload.enableBetaAccess === false
  ) {
    const betaFlagValue = action.payload.enableBetaAccess;
    if (betaFlagValue !== undefined && !betaFlagValue)
      return AppMode.BETA_NOT_ALLOWED;
  }

  // treat a failure to get org polices + the lack of permissions as an indicator that
  // the user is not verified and part of an org that has policy restrictions
  if (
    isActionOf(userActions.getOrgPolicies.failure, action) &&
    state.context.user.profile.permissions.read.length === 0
  ) {
    return AppMode.NOT_VERIFIED_POLICY_RESTRICTED;
  } else if (
    state.context.config.featureFlags.validateEmailNotification &&
    isActionOf(userActions.getUserProfile.success, action) &&
    !state.context.user.skipEmailValidation &&
    [AppMode.DASHBOARD, AppMode.LOADING_DASHBOARD].includes(state.mode) &&
    !action.payload.emailValidated
  ) {
    return AppMode.SKIPPABLE_VERIFIED_POLICY_RESTRICTED;
  }

  if (isErrorAction(state, action)) {
    return AppMode.ERROR;
  }

  return state.mode;
}

const isErrorAction = (state: AppState, action: RootAction) => {
  return (
    isGetWorkspaceDetailsFailure(state, action) ||
    isAddWorkspaceToProjectFailure(action)
  );
};

const isGetWorkspaceDetailsFailure = (state: AppState, action: RootAction) => {
  if (isActionOf(workspacesActions.getWorkspaceDetails.failure, action)) {
    if (
      action.payload.workspaceId === state.context.workspaces.waitingToEditID &&
      (action.payload.error as AjaxError).status !== 401
    )
      return true;
  }
  return false;
};

const isAddWorkspaceToProjectFailure = (action: RootAction) => {
  return isActionOf(workspacesActions.addWorkspaceToProject.failure, action);
};

function fromLoadingDashboard(context: AppContext, action: RootAction) {
  if (isActionOf(workspacesActions.editWorkspace, action)) {
    const workspaceDetail = selectDashboardWorkspaceDetailsById(
      { context, mode: AppMode.DASHBOARD },
      action.payload.workspaceId,
    );

    if (workspaceDetail) {
      if (workspaceDetail.isChannelReady) {
        return AppMode.EDITING;
      }
      return AppMode.LOADING_WORKSPACE;
    } else if (context.workspaces.waitingToEditID) {
      return AppMode.LOADING_WORKSPACE;
    }
    return AppMode.ERROR;
  }

  if (isActionOf(userActions.getToken.failure, action))
    return AppMode.NOT_LOGGED_IN;
  if (isActionOf(userActions.getLicenseInfo.success, action)) {
    if (context.user.licenseInfo) {
      return AppMode.DASHBOARD;
    } else {
      return AppMode.LOADING_DASHBOARD;
    }
  }
  if (isActionOf([workspacesActions.loadWorkspaces.success], action)) {
    return context.workspaces.waitingToEditID
      ? AppMode.LOADING_WORKSPACE
      : AppMode.DASHBOARD;
  }

  return AppMode.LOADING_DASHBOARD;
}

function fromNotLoggedIn(_context: AppContext, action: RootAction) {
  if (
    isActionOf(
      [userActions.login.success, userActions.registerEmail.success],
      action,
    )
  )
    return AppMode.LOADING_DASHBOARD;
  return AppMode.NOT_LOGGED_IN;
}

function fromDashboard(context: AppContext, action: RootAction) {
  if (isActionOf(workspacesActions.editWorkspace, action)) {
    const workspaceDetail = selectDashboardWorkspaceDetailsById(
      { context, mode: AppMode.DASHBOARD },
      action.payload.workspaceId,
    );

    if (workspaceDetail) {
      if (workspaceDetail.isChannelReady) {
        return AppMode.EDITING;
      }
      return AppMode.LOADING_WORKSPACE;
    } else if (context.workspaces.waitingToEditID) {
      return AppMode.LOADING_WORKSPACE;
    }
    return AppMode.ERROR;
  }
  if (isActionOf(workspacesActions.queueEditWorkspace, action)) {
    const workspace = selectDashboardWorkspaceDetailsById(
      { context, mode: AppMode.DASHBOARD },
      action.payload.workspaceId,
    );

    if (workspace) {
      if (workspace.isChannelReady) {
        return AppMode.EDITING;
      }
      return AppMode.LOADING_WORKSPACE;
    }
  }
  if (isActionOf(workspacesActions.addWorkspaceToProject.failure, action)) {
    return AppMode.ERROR;
  }
  if (
    isActionOf(retryableFailures, action) &&
    (action.payload.error as AjaxError).status !== 401
  ) {
    return AppMode.ERROR;
  }

  return AppMode.DASHBOARD;
}

function fromLoadingWorkspace(context: AppContext, action: RootAction) {
  if (isActionOf(workspacesActions.workspaceShowingPrompt, action)) {
    return AppMode.EDITING;
  }
  if (
    isActionOf(
      [
        workspacesActions.workspaceChannelReady,
        workspacesActions.workspaceDocumentReady,
      ],
      action,
    )
  ) {
    const workspaceDetail = selectDashboardWorkspaceDetailsById(
      { context, mode: AppMode.LOADING_WORKSPACE },
      action.payload,
    );

    if (!workspaceDetail?.isChannelReady) {
      return AppMode.LOADING_WORKSPACE;
    }

    // FIXME: Redux reducers are to be considered pure functions and the below two conditionals issue side-effects. These should be moved to some middleware listeners.

    if (context.config.teamsFrame === FrameContexts.settings) {
      microsoftTeams.getContext((context) => {
        microsoftTeams.settings.registerOnSaveHandler((saveEvent) => {
          microsoftTeams.settings.setSettings({
            websiteUrl: window.location.href,
            contentUrl: appendTeamsMode(window.location.href),
            entityId: context.entityId,
            suggestedDisplayName: document.title,
          });
          saveEvent.notifySuccess();
        });
      });
      microsoftTeams.settings.setValidityState(true);
    }

    if (context.config.teamsFrame === FrameContexts.sidePanel) {
      sendMessageToWorkspace(action.payload, {
        action: "TOGGLE_FOCUS",
        isFocused: false,
      });
    }

    return AppMode.EDITING;
  }

  if (
    isActionOf(
      [
        workspacesActions.showDashboard,
        workspacesActions.createWorkspaceFromTemplate,
      ],
      action,
    )
  )
    return AppMode.DASHBOARD;
  if (
    isActionOf(workspacesActions.activateDashboardOption, action) &&
    action.payload.optionType === DashboardOption.UPSELL
  ) {
    return AppMode.DASHBOARD;
  }

  if (isActionOf(userActions.userIdUpdated, action)) {
    if (!context.user.isReloggedUser) {
      return AppMode.DASHBOARD;
    }
  }

  return AppMode.LOADING_WORKSPACE;
}

function fromEditing(context: AppContext, action: RootAction) {
  if (isActionOf(workspacesActions.workspaceChannelReady, action)) {
    // Case: correct workspace pw has been submitted
    const workspace = selectWorkspaceById(
      { context, mode: AppMode.EDITING },
      action.payload,
    );

    if (workspace?.hasPassword) {
      return AppMode.LOADING_WORKSPACE;
    }
  }

  if (
    isActionOf(
      [
        workspacesActions.showDashboard,
        workspacesActions.deleteWorkspace.success,
        workspacesActions.toggleWorkspaceVisibility.success,
        workspacesActions.leaveWorkspace.success,
      ],
      action,
    )
  ) {
    if (
      isActionOf(
        [
          workspacesActions.deleteWorkspace.success,
          workspacesActions.toggleWorkspaceVisibility.success,
          workspacesActions.leaveWorkspace.success,
        ],
        action,
      )
    ) {
      // FIXME: Side-effect
      updateDocumentTitle("Hoylu");
    }
    return AppMode.DASHBOARD;
  }

  if (
    isActionOf(
      [
        workspacesActions.setWorkspacePassword.failure,
        workspacesActions.removeWorkspacePassword.failure,
      ],
      action,
    ) &&
    (action.payload.error as AjaxError).status !== 401
  ) {
    return AppMode.ERROR;
  }
  return AppMode.EDITING;
}

function fromError(context: AppContext, action: RootAction) {
  if (
    isActionOf(modeActions.clearError, action) &&
    context.user.isLoggedIn === true
  ) {
    return AppMode.DASHBOARD;
  }
  return AppMode.ERROR;
}

function fromNotVerified(state: AppState, action: RootAction) {
  if (isActionOf(userActions.sendValidationEmail.failure, action))
    return AppMode.ERROR;
  if (isActionOf(userActions.sendValidationEmail.success, action))
    return AppMode.NOT_LOGGED_IN;
  if (isActionOf(userActions.skipValidationEmail.request, action))
    return AppMode.DASHBOARD;
  return state.mode;
}
